import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useStillCurrent from 'CustomHooks/useStillCurrent';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Button from 'Components/Atoms/Button';
import { CancelOutlined } from '@material-ui/icons';
import Box from 'Components/Atoms/Box';
import Typography from 'Components/Atoms/Typography';
import GTable, { ContextAction, ListConfigHeader } from 'Components/Organisms/GTable';
import useAlert from 'CustomHooks/useAlert';
import useToast from 'CustomHooks/useToast';
import { Severity } from 'Contexts/RestaurantContext';
import { AuthContext } from 'Contexts/AuthContext';
import { dateHelper, toEuropeanDate } from 'utils/helper';
import { apiMessage } from './visitWishApi';
import { ReleaseAlert, releaseAlertApi } from './releaseAlertApi';

const STATUS_LABELS: Record<ReleaseAlert['status'], string> = {
  pending_confirmation: 'Waiting for confirmation',
  active: 'Active',
  notified: 'Guest informed',
  withdrawn: 'Withdrawn',
  expired: 'Period over',
};

const range = (from: string, to: string) =>
  from === to ? toEuropeanDate(from) : `${toEuropeanDate(from)} – ${toEuropeanDate(to)}`;

/**
 * Release notifications: guests who asked to hear once when later dates open.
 * The message says reservations are open, never that a table is free. Whether
 * guests may ask for them is set in the app settings, not here.
 */
const ReleaseAlertsSection: React.FC<{ restaurantId: string }> = ({ restaurantId }) => {
  const { t } = useTranslation('reservations');
  const toast = useToast();
  const confirm = useAlert();
  // The API withdraws alerts for restaurant admins only.
  const { isAdmin } = useContext(AuthContext);
  const [alerts, setAlerts] = useState<ReleaseAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const small = useMediaQuery('(max-width:600px)');

  const stillShown = useStillCurrent(restaurantId);

  const load = useCallback(async () => {
    const requestedFor = restaurantId;
    setLoading(true);
    try {
      const list = await releaseAlertApi.list(requestedFor);
      if (!stillShown(requestedFor)) return;
      setAlerts(list);
      setLoadFailed(false);
    } catch (error) {
      if (stillShown(requestedFor)) {
        setLoadFailed(true);
        toast(apiMessage(error, 'Loading failed'), Severity.ERROR, 'reservations');
      }
    } finally {
      if (stillShown(requestedFor)) setLoading(false);
    }
    // toast only reports; a new toast function must not load again.
  }, [restaurantId, stillShown]);

  useEffect(() => {
    load();
  }, [load]);

  const withdraw = async (alert: ReleaseAlert) => {
    setWithdrawing(alert.id);
    try {
      const updated = await releaseAlertApi.withdraw(restaurantId, alert.id);
      setAlerts((current) => current.map((entry) => (entry.id === updated.id ? updated : entry)));
    } catch (error) {
      toast(apiMessage(error, 'Action failed'), Severity.ERROR, 'reservations');
    } finally {
      setWithdrawing(null);
    }
  };

  const headers: ListConfigHeader<ReleaseAlert>[] = [
    {
      field: 'period',
      headerName: 'Requested period',
      headerTranslation: 'reservations',
      displayFunction: (alert) => (
        <Typography variant="text-3" weight="medium">
          {range(alert.dateFrom, alert.dateTo)}
        </Typography>
      ),
    },
    {
      field: 'name',
      headerName: 'Guest',
      headerTranslation: 'reservations',
      displayFunction: (alert) => (
        <Typography variant="text-3">
          {alert.contact.name}
          <br />
          <Typography variant="text-4" color="subdued">
            {alert.contact.email}
            {alert.channels?.sms && alert.contact.phone
              ? ` · ${t('SMS')}: ${alert.contact.phone}`
              : ''}
          </Typography>
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      headerTranslation: 'reservations',
      width: 170,
      displayFunction: (alert) => (
        <Typography variant="text-3" translation="reservations">
          {STATUS_LABELS[alert.status]}
        </Typography>
      ),
    },
    {
      field: 'notified',
      headerName: 'Announced as open',
      headerTranslation: 'reservations',
      width: 200,
      displayFunction: (alert) => (
        <Typography variant="text-3" color="subdued">
          {alert.notified
            ? (alert.notified.openSpans ?? [alert.notified])
                .map((span) => range(span.from, span.to))
                .join(', ')
            : '–'}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Received',
      headerTranslation: 'reservations',
      width: 100,
      displayFunction: (alert) => (
        <Typography variant="text-4" color="subdued">
          {toEuropeanDate(dateHelper(alert.createdAt))}
        </Typography>
      ),
    },
  ];

  const contextActions: ContextAction<ReleaseAlert>[] = [
    {
      icon: <CancelOutlined color="inherit" />,
      iconColor: 'inherit',
      tooltipContent: 'Stop release alert',
      tooltipContentTranslation: 'reservations',
      showCondition: (alert) =>
        !!isAdmin && (alert.status === 'pending_confirmation' || alert.status === 'active'),
      onClick: (alert) =>
        confirm({
          title: 'Stop this release notification?',
          titleTranslation: 'reservations',
          description: 'The guest will not be told when the dates open.',
          descriptionTranslation: 'reservations',
          severity: 'danger',
          onSubmit: () => withdraw(alert),
        }),
    },
  ];

  return (
    <Box data-testid="release-alerts" aria-busy={loading}>
      <Typography
        variant="text-3"
        color="subdued"
        translation="reservations"
        block
        style={{ margin: '8px 0 16px' }}
      >
        Guests outside the booking window are told once when their dates open. No table is held.
      </Typography>
      {loadFailed && (
        <div className="visit-wish-state" role="alert">
          <Typography variant="text-3" color="error" translation="reservations">
            Loading failed
          </Typography>
          <Button variant="outlined" translation="reservations" onClick={load} disabled={loading}>
            Try again
          </Button>
        </div>
      )}
      {small ? (
        loading ? (
          <Typography variant="text-3" translation={null} role="status">
            {t('Loading…')}
          </Typography>
        ) : alerts.length ? (
          <ul className="visit-wish-cards">
            {alerts.map((alert) => (
              <li className="visit-wish-card visit-wish-release-card" key={alert.id}>
                <Typography variant="text-3" weight="medium" block>
                  {alert.contact.name}
                </Typography>
                <Typography variant="text-3" block>
                  {range(alert.dateFrom, alert.dateTo)}
                </Typography>
                <Typography variant="text-3" color="subdued" translation="reservations" block>
                  {STATUS_LABELS[alert.status]}
                </Typography>
                <a href={`mailto:${encodeURIComponent(alert.contact.email)}`}>
                  {alert.contact.email}
                </a>
                {alert.channels?.sms && alert.contact.phone && (
                  <a href={`tel:${alert.contact.phone.replace(/[^+0-9]/g, '')}`}>
                    {alert.contact.phone}
                  </a>
                )}
                {alert.notified && (
                  <Typography variant="text-4" color="subdued" block>
                    {t('Announced as open')}:{' '}
                    {(alert.notified.openSpans ?? [alert.notified])
                      .map((span) => range(span.from, span.to))
                      .join(', ')}
                  </Typography>
                )}
                {contextActions[0].showCondition?.(alert) && (
                  <Button
                    variant="outlined"
                    translation="reservations"
                    disabled={withdrawing === alert.id}
                    onClick={() => contextActions[0].onClick(alert)}
                  >
                    Stop release alert
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          !loadFailed && (
            <Typography variant="text-3" color="subdued" translation="reservations" role="status">
              No release notifications yet
            </Typography>
          )
        )
      ) : (
        <div className="visit-wish-table visit-wish-scroll-table">
          <GTable
            headers={headers}
            data={loading ? [] : alerts}
            contextActions={contextActions}
            emptySentence={
              loadFailed
                ? t('Loading failed')
                : loading
                  ? t('Loading…')
                  : t('No release notifications yet')
            }
          />
        </div>
      )}
    </Box>
  );
};

export default ReleaseAlertsSection;
